import React, { useEffect, useState } from 'react';
import { MarketplaceData, getMarketplaceApps } from 'app/console-api/marketplace/marketplace';
import { StyledTableHeader } from 'app/components/common/layout/styled-elements';
import { FlexColumn, FlexRow, HeaderText, SvgIcon, TableRow, TableRowType } from '@make-software/cspr-design';
import { PreloadTableWindow } from 'app/components/common/preload-table-window/preload-table-window';
import NoFound from 'assets/logos/no-found.svg';
import Table from 'app/components/common/table/table';
import BaseTableHeader from 'app/components/common/base-table/base-table-header';
import {
  LayoutContainer,
  StyledTableFiltersBlock,
  TablePageContainer
} from 'app/components/common/common-styles';
import { PaginationResponseType } from 'app/console-api';
import { Pagination, ErrorResult } from 'app/organizations-api/types';
import { MarketplaceAppItem } from 'app/components/marketplace/marketplace-app-item';
import { DealTypesFilter, DealTypesAdapter } from 'app/components/filters/deal-types-filter';
import { MarketplaceNameFilter, MarketplaceNameAdapter } from 'app/components/filters/marketplace-name-filter';
import { MauFilter, MauMinAdapter, MauMaxAdapter } from 'app/components/filters/mau-filter';
import { MarketplaceCategoriesFilter, MarketplaceCategoriesAdapter } from 'app/components/filters/marketplace-categories-filter';
import { MarketplaceSortFilter, SortByAdapter } from 'app/components/filters/marketplace-sort-filter';
import { FilterAdapter, useUrlFilterController } from 'app/components/filters/url-filters';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { SIGN_IN_PATH } from 'app/route/paths';
import { TradeType } from 'app/console-api/offers/offers';

const ALL_ADAPTERS: FilterAdapter<any>[] = [
  MarketplaceNameAdapter,
  MarketplaceCategoriesAdapter,
  MauMinAdapter,
  MauMaxAdapter,
  DealTypesAdapter,
  SortByAdapter
];

export const MarketplaceAppsList = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<Pagination>({ currentPage: 1, pageSize: 10 });
  const [marketplaceApps, setMarketplaceApps] =
    useState<PaginationResponseType<MarketplaceData> | null>(null);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);

  const ctl = useUrlFilterController();
  const filterCacheKey = ctl.buildCacheKey(ALL_ADAPTERS);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [filterCacheKey]);

  useEffect(() => {
    setLoading(true);
    getMarketplaceApps(pagination, {
      name: ctl.get(MarketplaceNameAdapter) ?? undefined,
      categories: ctl.get(MarketplaceCategoriesAdapter) ?? undefined,
      mauMin: ctl.get(MauMinAdapter) ?? undefined,
      mauMax: ctl.get(MauMaxAdapter) ?? undefined,
      tradeTypes: (ctl.get(DealTypesAdapter) as TradeType[]) ?? undefined,
      sortBy: ctl.get(SortByAdapter) ?? undefined
    })
      .then((response) => {
        setMarketplaceApps(response);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.validation_error:
              setUnexpectedError({ code: error.statusCode.toString(), message: error.message });
              break;
            default:
              setUnexpectedError(customErrorMessage);
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to fetch marketplace apps:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [pagination, filterCacheKey]);

  const handlePageActions = (page: number, limit: number) => {
    if (pagination.pageSize !== limit) {
      page = 1;
    }
    setPagination((prev) => ({ ...prev, currentPage: page, pageSize: limit }));
  };

  const noApps = !marketplaceApps?.data?.length && !loading && !unexpectedError;

  return (
    <LayoutContainer>
      <StyledTableHeader justify={'space-between'}>
        <HeaderText size={1} scale={'xs'}>
          Marketplace
        </HeaderText>
      </StyledTableHeader>
      <TablePageContainer>
        <FlexColumn>
          <FlexRow align={'center'} justify={'space-between'} >
            <StyledTableFiltersBlock>
              <MarketplaceNameFilter />
              <MarketplaceCategoriesFilter />
              <DealTypesFilter />
              <MauFilter />
            </StyledTableFiltersBlock>
            <StyledTableFiltersBlock>
              <MarketplaceSortFilter />
            </StyledTableFiltersBlock>
          </FlexRow>
          {noApps ? (
            <PreloadTableWindow
              icon={<SvgIcon src={NoFound} height={120} width={224} />}
              title="No apps yet"
            />
          ) : (
            <Table
              loading={loading}
              data={marketplaceApps?.data || []}
              pageCount={marketplaceApps?.page_count || 1}
              error={unexpectedError}
              pageSize={pagination.pageSize}
              currentPage={pagination.currentPage}
              handlePageActions={handlePageActions}
              tableRowType={TableRowType.TextSingleLine}
              renderDataHeader={() => (
                <TableRow>
                  <BaseTableHeader fixedWidthRem={40}>App</BaseTableHeader>
                  <BaseTableHeader fixedWidthRem={30}>Details</BaseTableHeader>
                  <BaseTableHeader fixedWidthRem={20}>Audience</BaseTableHeader>
                  <BaseTableHeader fixedWidthRem={20}>Offers</BaseTableHeader>
                  <BaseTableHeader align="right">Action</BaseTableHeader>
                </TableRow>
              )}
              renderPaginatedData={(items) =>
                items.map((item) => <MarketplaceAppItem key={item.app.id} data={item} />)
              }
            />
          )}
        </FlexColumn>
      </TablePageContainer>
    </LayoutContainer>
  );
};
